import {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useState,
} from 'react'
import {
  IBenefitsContext,
  IBenefitsDetails,
  IBenefitsList,
  IFAQList,
  IPartnerLaboratoriesList,
} from '../benefits-types'
import httpClient from '@/services/httpClient'
import { toast } from '@conexasaude/hero'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'

const BenefitsContext = createContext<IBenefitsContext>(null)

export const BenefitsProvider: FunctionComponent = ({ children }) => {
  const [benefitsList, setBenefitsList] = useState<IBenefitsList[]>(null)
  const [loading, setLoading] = useState(false)
  const [faqList, setFaqList] = useState<IFAQList[]>(null)
  const { closeModal } = useModalContext()
  const [healthInsuranceSummary, setHealthInsuranceSummary] =
    useState<IBenefitsDetails>(null)
  const [partnerLaboratoriesList, setPartnerLaboratoriesList] =
    useState<IPartnerLaboratoriesList[]>(null)

  const getBenefitsList = useCallback(async () => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar`,
      )

      setBenefitsList(response.data.object)
    } catch (err) {
      const { msg } = err.response.data
      toast(msg, {
        variant: 'danger',
        autoClose: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const getFAQList = useCallback(async idBenefits => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/faq/${idBenefits}`,
      )

      setFaqList(response.data.object)
    } catch (err) {
      const { msg } = err.response.data
      toast(msg, {
        variant: 'danger',
        autoClose: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const handleAcceptTerm = useCallback(
    async idBenefits => {
      try {
        await httpClient.post(
          '/integration/app-paciente/beneficio/aceitar-termo',
          {
            idTipoBeneficio: idBenefits,
          },
        )

        closeModal()
        getBenefitsList()
      } catch (err) {
        const { msg } = err.response.data
        toast(msg, {
          variant: 'danger',
          autoClose: 5000,
          position: 'top-right',
        })
      }
    },
    [closeModal, getBenefitsList],
  )
  const getFilterFAQList = useCallback(async (idBenefits, filterText) => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/faq/${idBenefits}/filtro/${filterText}`,
      )

      setFaqList(response.data.object)
    } catch (err) {
      const { msg } = err.response.data
      toast(msg, {
        variant: 'danger',
        autoClose: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const getHealthInsuranceSummary = useCallback(async idBenefits => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar/${idBenefits}`,
      )

      const [benefit] = response.data.object

      setHealthInsuranceSummary(benefit.detalhes)
    } catch (err) {
      const { msg } = err.response.data
      toast(msg, {
        variant: 'danger',
        autoClose: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  const getPartnerLaboratories = useCallback(async () => {
    try {
      setLoading(true)
      const response = await httpClient.get(
        `/integration/app-paciente/beneficio/listar/laboratorio-parceiro`,
      )

      setPartnerLaboratoriesList(response.data.object)
    } catch (err) {
      const { msg } = err.response.data
      toast(msg, {
        variant: 'danger',
        autoClose: 5000,
        position: 'top-right',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <BenefitsContext.Provider
      value={{
        benefitsList,
        faqList,
        loading,
        healthInsuranceSummary,
        partnerLaboratoriesList,
        handleAcceptTerm,
        getBenefitsList,
        getFAQList,
        getFilterFAQList,
        getHealthInsuranceSummary,
        getPartnerLaboratories,
      }}
    >
      {children}
    </BenefitsContext.Provider>
  )
}

export const useBenefitsContext = (): IBenefitsContext => {
  const context = useContext(BenefitsContext)

  if (!context) {
    throw new Error('useBenefitsContext must be used within a BenefitsProvider')
  }

  return context
}
